import { forwardRef } from 'react';
import { useUserPreferences } from '../../hooks';
import { BuzzPin } from '../icons/BuzzPin';
import styles from './LocationPill.module.css';

/** Shown when no location is set — a call to action, not a description of a state. */
export const NO_LOCATION_LABEL = 'Seleziona la tua città';

/**
 * T015-FE: a classified migration left the identity NULL
 * (`locationNeedsConfirmation`, plan l.79) — the pill must ask for the choice
 * instead of reading as an unselected "Seleziona la tua città", and the popup
 * it opens carries the banner + the independent saved radius. The flag is
 * `false` outside v2 by construction, so legacy labels stay byte-identical.
 */
export const CONFIRM_LOCATION_LABEL = 'Conferma la tua località';

export interface LocationPillProps {
  onClick: () => void;
  variant?: 'header' | 'inline';
}

/**
 * Shared location pill button.
 * Reads `effectiveLocation` (the resolved territory in v2, otherwise the
 * session override or the saved preference) and renders a label with a
 * BuzzPin prefix.
 *
 * Label rules (radius appended only when location is NOT 'none'):
 *   - 'none'                 -> "Seleziona la tua città" (an invitation, not a state:
 *                              "Italia" read as a completed choice nobody needed to touch,
 *                              and almost no ad visitor ever opened the picker)
 *   - needs confirmation     -> "Conferma la tua località" (T015-FE, see above)
 *   - 'cities' (1)           -> "{cityName} · {radius}km"
 *   - 'cities' (n>1)         -> "{first.name} +{n-1} · {radius}km"
 *   - 'gps'                  -> "Posizione attuale · {radius}km"
 *
 * T011-FE: the `useNearestCity` side-effect that resolved and PERSISTED a
 * derived comune name from raw coordinates is gone with the hook itself — a
 * nearest-name result is no longer an authority anywhere. A `derivedCityName`
 * already present in an old v1 record is still displayed (read-only) so
 * pre-activation labels do not change.
 */
export const LocationPill = forwardRef<HTMLButtonElement, LocationPillProps>(function LocationPill(
  { onClick, variant = 'header' },
  ref
) {
  const { effectiveLocation, locationNeedsConfirmation } = useUserPreferences();
  const location = effectiveLocation;

  const label = locationNeedsConfirmation ? CONFIRM_LOCATION_LABEL : computeLabel(location);
  const isCallToAction = locationNeedsConfirmation || label === NO_LOCATION_LABEL;

  const className = [styles.pill, variant === 'header' ? styles.header : styles.inline].join(' ');

  return (
    <button
      ref={ref}
      type="button"
      className={className}
      onClick={onClick}
      aria-label={isCallToAction ? label : `Modifica località: ${label}`}
    >
      <BuzzPin size={18} className={styles.icon} />
      <span className={styles.label}>{label}</span>
    </button>
  );
});

function computeLabel(
  location: ReturnType<typeof useUserPreferences>['preferences']['location']
): string {
  if (location.type === 'none') return NO_LOCATION_LABEL;
  if (location.type === 'cities') {
    if (location.cities.length === 0) return NO_LOCATION_LABEL;
    const first = location.cities[0]!.name;
    const base = location.cities.length === 1 ? first : `${first} +${location.cities.length - 1}`;
    return `${base} · ${location.radiusKm}km`;
  }
  // gps
  const name = location.derivedCityName?.trim();
  const base = name && name.length > 0 ? name : 'Posizione attuale';
  return `${base} · ${location.radiusKm}km`;
}
